import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {Icon, Button} from '@/components/atoms';
import './notificationalert.scss';

/**
 * NotificationAlert component to display notification messages.
 *
 * @param {Object} props - The properties object.
 * @param {'success' | 'error' | 'info' | 'warning'} props.type - The type of notification to display.
 * @param {Object} props.message - The message object containing title and subTitle.
 * @param {string} [props.message.title] - The title of the notification.
 * @param {string} [props.message.subTitle] - The subtitle/content of the notification.
 * @param {boolean} [props.show=false] - Whether the notification is visible or not.
 * @param {Function} [props.onCloseClick] - The function to call when the close button is clicked.
 * @param {Function} [props.handleClick] - The function to call when the action button is clicked.
 * @param {string} [props.iconType] - The icon type to display in the notification.
 * @param {boolean} [props.arrowButton] - Flag to determine if arrow button should be shown.
 * @param {boolean} [props.buttonShow=false] - Flag to determine if action button should be shown.
 * @param {Function} [props.handleArrow] - The function to call when the arrow button is clicked.
 *
 * @returns {JSX.Element|null} The NotificationAlert component or null if not visible.
 */

const NotificationAlert = ({
  type,
  message,
  show = false,
  onCloseClick,
  handleClick,
  iconType,
  arrowButton,
  buttonShow = false,
  handleArrow
}) => {
  useEffect(() => {
    if (!show) return;
  }, [message, onCloseClick, show, type]);

  if (!show) return null;

  return (
    <div
      data-testid={`alert-${type}`}
      className={`alert-toast-notification alert-${type} my-4 gap-4`}
      role='alert'>
      <div className='-mr-6 mt-1.5 inline-flex h-6 w-6 items-center justify-center rounded-lg'>
        <Icon icon={iconType} size='large' isDark={false} />
      </div>
      <div className='text-md ml-3 flex-1 items-start py-0 pl-1'>
        {message && (
          <div
            className={`flex items-center justify-between text-sm text-white`}>
            {' '}
            {/* Added justify-between to align button to the right */}
            <div>
              <div className='title font-bold'>{message.title}</div>{' '}
              {/* First line in bold */}
              <div className='sub-title'>{message.subTitle}</div>{' '}
              {/* Second line in normal text */}
            </div>
            {!buttonShow &&
              (arrowButton ? (
                <Button
                  handleButton={handleArrow}
                  uiSchema={{
                    className:
                      'button-arrow ml-4 mt-1 inline-flex items-center justify-center text-icon',
                    title: <Icon icon='arrow_forward' size='large' />,
                    size: 'medium',
                    variant: 'primary'
                  }}
                />
              ) : (
                <Button
                  handleButton={handleClick}
                  uiSchema={{
                    className:
                      'button-close ml-4 mt-1 inline-flex items-center justify-center text-icon',
                    title: 'Close',
                    size: 'medium',
                    variant: 'primary'
                  }}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

NotificationAlert.propTypes = {
  type: PropTypes.oneOf(['success', 'error', 'info', 'warning']).isRequired,
  message: PropTypes.shape({
    title: PropTypes.string,
    subTitle: PropTypes.string
  }).isRequired,
  show: PropTypes.bool,
  onCloseClick: PropTypes.func,
  handleClick: PropTypes.func,
  iconType: PropTypes.string,
  arrowButton: PropTypes.bool,
  buttonShow: PropTypes.bool,
  handleArrow: PropTypes.func
};

export default React.memo(NotificationAlert);
